import apiRequest from 'api/apiRequest';
import endpoints from 'api/endpoints';
import { AppBundle } from 'api/optimalprint-sdk.d';
import { HttpMethods } from 'api/types';
import { STUDIO_SEARCH_PAGE_SIZE } from 'constants/app';
import { AppDispatch, Store } from 'store';
import getLocaleSelector from 'store/app/selector/getLocaleSelector';
import { GettyImagesImageSearchResponse } from 'store/studioSearch/types';
import getFilterValue from 'utils/getFilterValue';
import removeEmptyObjectProperties from 'utils/removeEmptyObjectProperties';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmGettyNoResultsEvent from 'utils/gtm/gtmSearchGettyNoResultsEvent';
import getStudioSearchFilters from '../selector/getStudioSearchFilters';
import getStudioSearchPageNr from '../selector/getStudioSearchPageNr';
import getStudioSearchQuery from '../selector/getStudioSearchQuery';
import getStudioSearchRefetchFlag from '../selector/getStudioSearchRefetchFlag';
import getStudioSearchSortBy from '../selector/getStudioSearchSortBy';
import {
  resetStudioSearchResultsAction,
  setStudioSearchFiltersSelectionAction,
  setStudioSearchLoadingAction,
  setStudioSearchRefetchFlagAction,
  setStudioSearchResultsAction,
} from '../slice';

const fetchStudioSearchResultsOperation =
  (targetPage?: number) => async (dispatch: AppDispatch, getState: () => Store) => {
    dispatch(setStudioSearchLoadingAction(true));

    const store = getState();
    const currentPageNr = targetPage || getStudioSearchPageNr(store);
    const currentStudioSearchFilters = getStudioSearchFilters(store);
    const categoryStudioSearchFilters = store.studioSearch.categoryFilter;
    const getCombinedFilterValue = (name: string, defaultValue?: unknown, singleValue?: boolean) =>
      getFilterValue(
        name,
        currentStudioSearchFilters,
        categoryStudioSearchFilters
          ? getFilterValue(name, categoryStudioSearchFilters, defaultValue, singleValue)
          : defaultValue,
      );

    try {
      const searchResults = (await apiRequest(
        endpoints.gettyImagesV1Search,
        removeEmptyObjectProperties({
          phrase: getStudioSearchQuery(store),
          page: currentPageNr,
          pageSize: STUDIO_SEARCH_PAGE_SIZE,
          sortOrder: getStudioSearchSortBy(store),
          locale: getLocaleSelector(store),
          color: getCombinedFilterValue('color', '', true),
          compositions: getCombinedFilterValue('compositions', []),
          graphicalStyles: getCombinedFilterValue('graphicalStyles', []),
          orientations: getCombinedFilterValue('orientation', []),
          minimum_size: getCombinedFilterValue('minimum_size', [], true),
        }),
        HttpMethods.POST,
        'data',
      )) as GettyImagesImageSearchResponse;

      if (getStudioSearchRefetchFlag(store)) {
        dispatch(setStudioSearchRefetchFlagAction(false));
        dispatch(resetStudioSearchResultsAction());
      }

      if (!searchResults.images.length) {
        gtmSendEvent(gtmGettyNoResultsEvent(getStudioSearchQuery(store)));
      }

      dispatch(
        setStudioSearchResultsAction({
          searchResults: searchResults.images,
          pageNr: currentPageNr,
          totalResultsAvailable: searchResults.resultCount,
          filters: searchResults.filters as AppBundle.Api.Response.Category.V3.CategoryDesignsListV3Response.Filters,
        }),
      );

      dispatch(setStudioSearchFiltersSelectionAction(searchResults.filters));
    } catch (e) {
      dispatch(setStudioSearchRefetchFlagAction(false));
      dispatch(resetStudioSearchResultsAction());
      throw e;
    } finally {
      dispatch(setStudioSearchLoadingAction(false));
    }
  };

export default fetchStudioSearchResultsOperation;
